import { Config } from '../config';
import { Platform } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';
import { HttpClient } from '@angular/common/http';
import { TokenStoreService } from './token-store.service';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { UpdatePasswordModel } from './../models/auth.model';
import {Observable, of, Subject, merge, timer, BehaviorSubject, map, filter, take } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
import { PaginatedResponse } from '../models/paginated-response.model';
import { LanguagesModel, UserModel, ReportUserPaylaod, FAQModel } from '../models/user.model';
import { NativeGeocoder, NativeGeocoderOptions, NativeGeocoderResult } from '@awesome-cordova-plugins/native-geocoder/ngx';
import { UserFiltersModel, LanguageResponseModel, LanguageModel, ContactModel } from '../../app/models/user.model';
import { tap, switchMap, startWith, distinctUntilChanged, retryWhen, mergeMap, publishReplay, refCount } from 'rxjs/operators';
@Injectable()

export class UserService extends HttpService {

    public user: UserModel;
    public userDetail: UserModel;
    private readonly user$: Observable<UserModel>;
    private readonly refreshUser = new Subject<boolean>();
    updatedUser = new Subject<UserModel>();
    options: NativeGeocoderOptions = {
        useLocale: true,
        maxResults: 5
    };

    constructor(
        http: HttpClient, authService: AuthService,
        private tokenStoreService: TokenStoreService,
        private geolocation: Geolocation,
        private errorHandler: ErrorHandlerService,
        private nativeGeocoder: NativeGeocoder,
        private platform: Platform
    ) {
        super(http);
        authService.onLoginChange.subscribe(() => this.user = null);

        this.user$ = merge(
            authService.onLoginChange,
            this.refreshUser.pipe(
                switchMap(() => authService.loggedIn)
            )
        ).pipe(
            startWith(false),
            distinctUntilChanged(),
            switchMap(
                loggedIn => loggedIn ?
                    this.http.get<UserModel>(this.buildUrl('user', 'me')) :
                    of<UserModel>(null)
            ),
            retryWhen(errors => errors.pipe(
                mergeMap((_, attempt) => timer((attempt + 1) * 500))
            )),
            publishReplay(1),
            refCount()
        );
    }

    get userData() {
        return this.user$;
    }

    get(): Observable<UserModel> {
        if (this.user) {
            return of(this.user);
        }

        return this.http.get<UserModel>(this.buildUrl('user', 'me')).pipe(
            tap(user => this.user = user)
        );
    }

    getUpdatedUser(userId?: number): Observable<UserModel> {
        return this.http.get<UserModel>(this.buildUrl('user', 'me', userId ? userId : '')).pipe(
            tap(user => this.user = user)
        );
    }

    getUserDetail(id: number) {
        return this.http.get<UserModel>(`${Config.apiUrl}/user/details/${id}`);
    }

    updateUser(user: UserModel) {
        return this.http.put<UserModel>(this.buildUrl('user', 'me'), user);
    }

    updatePassword(params: UpdatePasswordModel) {
        return this.http.post(this.buildUrl('user', 'change-password'), params);
    }

    updateLanguage(value: LanguageModel, params?: any) {
        return this.http.put<UserModel>(this.buildUrl('user', 'me'), value, {
            params: this.serialize(params)
        });
    }

    /**
     * Report user to admin
     *
     * @param request Requst of type ReportUserPayload
     */
    reportUser(request: ReportUserPaylaod, userId: number) {
        return this.http.post(this.buildUrl(`user/${userId}/report`), request);
    }

    /**
     * Return the current location of the user
     * @param callback return object of latitude and longitude
     */
    getCurrentLocation(callback) {
        this.geolocation.getCurrentPosition().then((resp: any) => {
            if (Config.fakeLocation) {
                callback(Config.fakeLocationData);
            } else {
                if (this.platform.is('android') || this.platform.is('ios')) {
                    this.nativeGeocoder.reverseGeocode(resp.coords.latitude, resp.coords.longitude, this.options)
                        .then((result: NativeGeocoderResult[]) => {
                            resp.location = result[0];
                            callback(resp);
                        })
                        .catch((error: any) => console.log(error));
                } else {
                    callback(resp);
                }
            }
        }).catch(error => this.errorHandler.handle(error));
    }

    getUsers(params) {
        return this.http.get<PaginatedResponse<UserModel>>(`${Config.apiUrl}/user/list`, {
            params: this.serialize(params)
        });
    }

    removeAccount(id?: number) {
        return this.http.delete<UserFiltersModel>(`${Config.apiUrl}/user/delete/${id}`).pipe(
            switchMap(() => this.tokenStoreService.updateToken(null))
        );
    }

    getFaqs(params: any) {
        return this.http.get<PaginatedResponse<FAQModel>>(`${Config.apiUrl}/faqs`, {
            params: this.serialize(params)
        });
    }

    contactUs(data) {
        return this.http.post<ContactModel>(`${Config.apiUrl}/contact_us`, data);
    }

    toggleNotification(body) {
        return this.http.put(this.buildUrl('user', 'me'), body);
    }

    toggleFollowUser(id: number) {
        return this.http.post<ContactModel>(`${Config.apiUrl}/follow/toggle/user/${id}`, {});
    }

    userFollowers(params) {
        return this.http.get<PaginatedResponse<UserModel>>(`${Config.apiUrl}/followers/user`, {
            params: this.serialize(params)
        });
    }

    userFollowing(params) {
        return this.http.get<PaginatedResponse<UserModel>>(`${Config.apiUrl}/follow/user`, {
            params: this.serialize(params)
        });
    }

    updateUserSubscriptionStatus(status) {
      return this.http.put<UserModel>(`${Config.apiUrl}/subscription/update-status`, status);
    }
    getLoggedInUserId(): Observable<number> {
        return this.userData.pipe(
            filter(user => !!user),
            take(1),
            map(user => user.id)
        );
    }

    // GET: Fetch ratings given to a user
        getUserRatings(user_id: number): Observable<any> {
            return this.http.get(`${Config.apiUrl}/rating/ratings/${user_id}`);
        }

        // POST: Submit a rating
        submitRating(payload: { rated_by: number; user_id: number; rating: number; comment: string, productId: number }): Observable<any> {
            return this.http.post(`${Config.apiUrl}/rating/ratings`, {
            rated_by: payload.rated_by,
            user_id: payload.user_id, // ID of the user being rated
            product_id: payload.productId,
            rating: payload.rating,
            comment: payload.comment
            });
        }
}
